import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import MatplotlibQuickPreview from "./MatplotlibQuickPreview";
import { useAuth } from "../../utils";
import { getOrRenderMatplotlibPreview } from "../../shared/matplotlibPreviewCache";
import { usePreviewActivity } from "../../shared/previewActivity";
import { getQuestionImageAlignment } from "../../features/questionImageAlignment";
import { handoutLabelAsset } from "../../features/handoutFigureLabels";
import usePreviewViewport from "../../shared/usePreviewViewport";
import { measurePreviewImageDimensions, getPreviewImageDimensions, previewImageDimensionKey, rememberPreviewImageDimensions, subscribePreviewImageDimensions } from "../../shared/previewImageDimensions";

const getAssetImageUrl = (asset) =>
  String(asset?._preview_url || asset?._previewUrl || asset?.url || "").trim();

const isPythonAsset = (asset) =>
  String(asset?.source_language || "").trim().toLowerCase() === "python" &&
  String(asset?.source_code || "").trim();

export const isPreviewableQuestionAsset = (asset) =>
  Boolean(getAssetImageUrl(asset) || isPythonAsset(asset));

export const getPreviewableQuestionAssets = (assets = [], role = null) =>
  assets
    .filter(isPreviewableQuestionAsset)
    .filter((asset) => !role || asset.role === role)
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

export default function QuestionAssetPreviewStrip({
  assets = [],
  compact = false,
  showCaption = true,
  limit = Infinity,
  role = null,
  getRoleLabel,
  defaultAlignment,
  imageAlignments = {},
  lazyRender = false,
  widthCm,
  bodyFontSize = 12,
  imageWidths = {},
  transparentBackground = false,
}) {
  const [expandedImage, setExpandedImage] = useState(null);
  const [renderedImages, setRenderedImages] = useState({});
  const [, setGeometryVersion] = useState(0);
  const documentSized = Number(widthCm) > 0;
  const { previewMatplotlibCode } = useAuth() || {};
  const previewAssets = getPreviewableQuestionAssets(assets, role).slice(0, limit)
    .map((asset) => documentSized ? handoutLabelAsset(asset, imageWidths[String(asset.id)] || widthCm, bodyFontSize) : asset);
  const pendingCodes = previewAssets
    .filter((asset) => isPythonAsset(asset) && !getAssetImageUrl(asset))
    .map((asset) => String(asset.source_code).trim());
  const pendingKey = pendingCodes.join("\u0000");
  const [viewportRef, inViewport] = usePreviewViewport(Boolean(lazyRender && compact && pendingKey));
  const pageActive = usePreviewActivity();

  useEffect(() => {
    const activeCodes = new Set(inViewport ? pendingKey.split("\u0000") : []);
    setRenderedImages((current) => Object.keys(current).every((code) => activeCodes.has(code))
      ? current : Object.fromEntries(Object.entries(current).filter(([code]) => activeCodes.has(code))));
  }, [pendingKey, inViewport]);

  useEffect(() => {
    if (!pageActive || !inViewport || !previewMatplotlibCode || !pendingKey) return undefined;
    let cancelled = false;
    // Leaving the viewport, unmounting or changing code drops queued renders.
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    pendingKey.split("\u0000").forEach((code) => {
      getOrRenderMatplotlibPreview(code, previewMatplotlibCode, { signal: controller?.signal })
        .catch((error) => ({
          success: false,
          error: error?.message || "後端圖片產生失敗",
        }))
        .then((result) => {
          if (!cancelled && !result?.aborted) {
            setRenderedImages((current) => ({ ...current, [code]: result }));
          }
        });
    });
    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [pendingKey, previewMatplotlibCode, inViewport, pageActive]);
  const geometryKeys = documentSized ? previewAssets.map(previewImageDimensionKey).join("\u0000") : "";
  useEffect(() => {
    if (!geometryKeys) return undefined;
    const keys = new Set(geometryKeys.split("\u0000"));
    const unsubscribe = subscribePreviewImageDimensions((key) => {
      if (keys.has(key)) setGeometryVersion((version) => version + 1);
    });
    // A cached image can finish before passive effects subscribe.
    if (Array.from(keys).some(getPreviewImageDimensions)) setGeometryVersion((version) => version + 1);
    return unsubscribe;
  }, [geometryKeys]);
  const geometrySources = documentSized ? JSON.stringify(previewAssets.map((asset) => [
    previewImageDimensionKey(asset), getAssetImageUrl(asset) || renderedImages[String(asset.source_code || "").trim()]?.image || "",
  ])) : "";
  useEffect(() => {
    if (geometrySources) JSON.parse(geometrySources).forEach(([key, source]) => {
      if (source) measurePreviewImageDimensions(key, source);
    });
  }, [geometrySources]);
  if (!previewAssets.length) return null;

  return (
    <>
      <AssetPreviewGrid ref={viewportRef} $compact={compact}>
        {previewAssets.map((asset, index) => {
        const imageUrl =
          getAssetImageUrl(asset) ||
          renderedImages[String(asset.source_code || "").trim()]?.image ||
          "";
        const pythonCode = isPythonAsset(asset)
          ? String(asset.source_code || "").trim()
          : "";
        const previewStatus = String(
          asset.preview_status ||
            asset._previewStatus ||
            (renderedImages[pythonCode]?.error
              ? "error"
              : !imageUrl && pythonCode && previewMatplotlibCode
                ? "loading"
                : ""),
        ).trim();
        const previewMessage = String(
          asset.preview_message || asset._previewMessage || renderedImages[pythonCode]?.error || "",
        ).trim();
        const canExpand = Boolean(
          imageUrl && previewStatus !== "loading" && previewStatus !== "error",
        );
        const key = `${asset.id || imageUrl || pythonCode.slice(0, 32)}-${index}`;
        const dimensionKey = previewImageDimensionKey(asset);
        const intrinsic = Number(asset.width_px) > 0 && Number(asset.height_px) > 0
          ? { width: Number(asset.width_px), height: Number(asset.height_px) } : getPreviewImageDimensions(dimensionKey);
        const ratio = intrinsic ? intrinsic.width / intrinsic.height : undefined;

          const card = (
            <AssetPreviewCard
              $transparent={transparentBackground}
              data-handout-image-size={documentSized ? intrinsic ? "ready" : "pending" : undefined}
              style={documentSized ? { width: `${Number(imageWidths[String(asset.id)] || widthCm)}cm`, maxWidth: "100%", border: 0, borderRadius: 0, background: "transparent", breakInside: "avoid" } : undefined}
              data-record-export-image-state={previewStatus === "error" ? "error" : previewStatus === "loading" || (pythonCode && !imageUrl) ? "pending" : "ready"}
              key={key}
              $compact={compact}
              $clickable={canExpand}
              role={canExpand ? "button" : undefined}
              tabIndex={canExpand ? 0 : undefined}
              aria-label={canExpand ? `放大${asset.alt_text || `題目圖片 ${index + 1}`}` : undefined}
              onClick={canExpand ? (event) => {
                event.stopPropagation();
                setExpandedImage({
                  src: imageUrl,
                  alt: asset.alt_text || `題目圖片 ${index + 1}`,
                });
              } : undefined}
              onKeyDown={canExpand ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  setExpandedImage({
                    src: imageUrl,
                    alt: asset.alt_text || `題目圖片 ${index + 1}`,
                  });
                }
              } : undefined}
            >
            {documentSized ? (imageUrl && previewStatus !== "loading" && previewStatus !== "error" ? (
              <AssetPreviewImage $transparent={transparentBackground || documentSized} src={imageUrl} alt={asset.alt_text || `題目圖片 ${index + 1}`}
                width={intrinsic?.width} height={intrinsic?.height} loading="lazy" decoding="async"
                style={{ width: "100%", height: "auto", maxHeight: "none" }}
                onLoad={(event) => rememberPreviewImageDimensions(dimensionKey, event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)} />
            ) : <DocumentImagePlaceholder style={{ aspectRatio: ratio, minHeight: ratio ? 0 : 74 }}>
              <strong>{previewStatus === "error" ? "圖片產生失敗" : !inViewport ? "移至圖片時載入" : "圖片載入中"}</strong>
            </DocumentImagePlaceholder>) : pythonCode ? (
              <PythonPreviewFrame $compact={compact} $transparent={transparentBackground}>
                {!inViewport && !imageUrl ? (
                  <PreviewState $status="deferred"><strong>移至圖片時載入</strong></PreviewState>
                ) : previewStatus === "loading" ? (
                  <PreviewState $status="loading">
                    <span />
                    <strong>後端圖片產生中</strong>
                  </PreviewState>
                ) : previewStatus === "error" ? (
                  <PreviewState $status="error">
                    <strong>後端圖片產生失敗</strong>
                    <small>{previewMessage || "請檢查 Python Figure Code。"}</small>
                  </PreviewState>
                ) : (
                  <MatplotlibQuickPreview
                    transparentBackground={transparentBackground}
                    badgeAction="Space + W 送後端"
                    badgeTitle="SVG 動態"
                    code={pythonCode}
                    compact={compact}
                    emptyTitle="SVG 動態預覽尚無可畫內容"
                    image={imageUrl}
                    pending={!imageUrl}
                    loading={false}
                    unsupportedLabel="送後端產生圖片"
                    emptyMessage="可即時預覽 plot、scatter、text、Circle、Rectangle、Polygon、PathPatch 與 hand_line 手繪 helper。"
                  />
                )}
              </PythonPreviewFrame>
            ) : (
              <AssetPreviewImage
                $transparent={transparentBackground}
                src={imageUrl}
                alt={asset.alt_text || `題目圖片 ${index + 1}`}
                loading="lazy"
                decoding="async"
                $compact={compact}
              />
            )}
            {!compact && showCaption && (
              <AssetPreviewMeta>
                {getRoleLabel ? getRoleLabel(asset.role) : asset.role || "素材"}
                {pythonCode ? " · Python" : ""}
                {asset.alt_text ? ` · ${asset.alt_text}` : ""}
              </AssetPreviewMeta>
            )}
            </AssetPreviewCard>
          );
          if (!defaultAlignment) return card;
          const alignment = getQuestionImageAlignment(asset, imageAlignments, defaultAlignment);
          return (
            <AssetAlignmentRow key={key} $alignment={alignment} data-question-image-alignment={alignment}>
              {card}
            </AssetAlignmentRow>
          );
        })}
      </AssetPreviewGrid>
      {expandedImage && createPortal(
        <ExpandedImageOverlay
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            setExpandedImage(null);
          }}
        >
          <ExpandedImage src={expandedImage.src} alt={expandedImage.alt} />
        </ExpandedImageOverlay>,
        document.body,
      )}
    </>
  );
}

const AssetPreviewGrid = styled.div`
  grid-column: 1 / -1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $compact }) => ($compact ? "5px" : "8px")};
  align-items: flex-start;
  margin-top: ${({ $compact }) => ($compact ? "5px" : "2px")};
`;

const AssetAlignmentRow = styled.div`
  display: flex;
  justify-content: ${({ $alignment }) => $alignment === "right" ? "flex-end" : $alignment === "center" ? "center" : "flex-start"};
  width: ${({ $alignment }) => $alignment === "inline" ? "auto" : "100%"};
  max-width: 100%;
  min-width: 0;
`;

const AssetPreviewCard = styled.figure`
  width: ${({ $compact }) => ($compact ? "74px" : "min(240px, 100%)")};
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 7px;
  background: ${({ $transparent }) => $transparent ? "transparent" : "var(--app-input-bg, #fff)"};
  cursor: ${({ $clickable }) => ($clickable ? "zoom-in" : "default")};

  &:focus-visible {
    outline: 2px solid var(--app-nav-active-bg, #202124);
    outline-offset: 2px;
  }
`;

const ExpandedImageOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1600;
  display: grid;
  place-items: center;
  padding: clamp(10px, 3vw, 32px);
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));
  cursor: zoom-out;
`;

const ExpandedImage = styled.img`
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: calc(100dvh - 2 * clamp(10px, 3vw, 32px));
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.45);
`;

const AssetPreviewImage = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  height: ${({ $compact }) => ($compact ? "74px" : "auto")};
  max-height: ${({ $compact }) => ($compact ? "74px" : "180px")};
  object-fit: contain;
  background: ${({ $transparent }) => $transparent ? "transparent" : "#fff"};
`;

const PythonPreviewFrame = styled.div`
  height: ${({ $compact }) => ($compact ? "74px" : "auto")};
  min-height: ${({ $compact }) => ($compact ? "74px" : "160px")};
  overflow: hidden;
  background: ${({ $transparent }) => $transparent ? "transparent" : "#fff"};

  > * {
    min-height: 100%;
  }
`;

const PreviewState = styled.div`
  min-height: 100%;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 7px;
  padding: 10px;
  background: ${({ $status }) => ($status === "error" ? "#fff4f4" : "#f6f7f9")};
  color: ${({ $status }) => ($status === "error" ? "#9f1d1d" : "#4b5563")};
  text-align: center;
  font-size: 12px;
  font-weight: 800;

  span {
    width: 22px;
    height: 22px;
    border: 3px solid rgba(75, 85, 99, 0.22);
    border-top-color: #4b5563;
    border-radius: 999px;
    animation: spin 0.8s linear infinite;
  }

  small {
    max-width: 100%;
    color: inherit;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const AssetPreviewMeta = styled.figcaption`
  padding: 5px 7px;
  border-top: 1px solid var(--app-border, #e5e7eb);
  color: var(--app-muted-text, #5f6368);
  font-size: 11px;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const DocumentImagePlaceholder = styled.div`
  display: grid; place-items: center; width: 100%; box-sizing: border-box;
  color: #64748b; background: #f6f7f9; font: 12px system-ui;
`;
